/**
 * Single source of truth for bundle contents-page layout. Both the frontend
 * preview and the backend PDF renderer consume this so column count, labels,
 * row content, page numbering, and the default subheading stay in lockstep.
 *
 * The frontend imports it via the same cross-tree path it uses for shared DTO
 * types — keep this module dependency-free so it stays portable.
 */

export type BundleIndexColumnId = 'tab' | 'date' | 'document' | 'pages';

export interface BundleIndexColumn {
  id: BundleIndexColumnId;
  label: string;
  /** Fraction of the table's usable width. Sums to 1 across all columns. */
  fractionWidth: number;
}

export const BUNDLE_INDEX_COLUMNS: readonly BundleIndexColumn[] = [
  { id: 'tab', label: 'Tab', fractionWidth: 0.08 },
  { id: 'date', label: 'Date', fractionWidth: 0.2 },
  { id: 'document', label: 'Document', fractionWidth: 0.56 },
  { id: 'pages', label: 'Pages', fractionWidth: 0.16 },
];

export const BUNDLE_INDEX_COLUMN_IDS: readonly BundleIndexColumnId[] = BUNDLE_INDEX_COLUMNS.map(
  (c) => c.id,
);

/**
 * Returns the active bundle index columns in canonical order, with each
 * `fractionWidth` re-normalized so the kept columns sum to 1. Falls back to
 * the full set if the selection is empty or contains no recognised ids.
 */
export function resolveBundleColumns(
  selectedIds: readonly BundleIndexColumnId[] | undefined,
): BundleIndexColumn[] {
  const selectedSet = new Set(selectedIds ?? BUNDLE_INDEX_COLUMN_IDS);
  const kept = BUNDLE_INDEX_COLUMNS.filter((c) => selectedSet.has(c.id));
  const source = kept.length > 0 ? kept : [...BUNDLE_INDEX_COLUMNS];
  const total = source.reduce((sum, c) => sum + c.fractionWidth, 0);
  return source.map((c) => ({ ...c, fractionWidth: c.fractionWidth / total }));
}

export const BUNDLE_DEFAULT_SUBHEADING = 'The High Court';

export interface BundleIndexInputItem {
  name: string;
  customName?: string | null;
  dateText?: string | null;
  pageCount: number;
}

export interface BundleIndexRow {
  tab: string;
  date: string;
  document: string;
  pages: string;
  startPage: number;
  endPage: number;
}

export function computeBundlePageLayout(
  itemPageCounts: number[],
): Array<{ startPage: number; endPage: number; label: string }> {
  let currentPage = 1;
  return itemPageCounts.map((count) => {
    const pages = Math.max(1, count);
    const startPage = currentPage;
    const endPage = startPage + pages - 1;
    currentPage = endPage + 1;
    return {
      startPage,
      endPage,
      label: startPage === endPage ? `p. ${startPage}` : `pp. ${startPage}–${endPage}`,
    };
  });
}

export function buildBundleIndexRows(items: BundleIndexInputItem[]): BundleIndexRow[] {
  const layout = computeBundlePageLayout(items.map((item) => item.pageCount));
  return items.map((item, index) => {
    const customName = item.customName?.trim() ?? '';
    return {
      tab: String(index + 1),
      date: item.dateText ?? '',
      document: customName !== '' ? customName : item.name,
      pages: layout[index].label,
      startPage: layout[index].startPage,
      endPage: layout[index].endPage,
    };
  });
}
